import { useCallback } from 'react'
import { useAppSelector } from '@/store/hooks'
import { matchesWildcard } from '@/features/role/utils/compute-permissions'

/**
 * 权限检查 Hook
 *
 * 支持的权限格式：
 * - 精确匹配: "pms:task:create"
 * - 超管通配符: "*:*:*" → 匹配所有
 * - 模块通配符: "pms:*" → 匹配 pms 下所有权限
 * - 资源通配符: "pms:task:*" → 匹配 pms:task 下所有操作
 *
 * @example
 * const { hasPermission } = usePermission()
 *
 * // 单个权限检查
 * hasPermission('pms:task:create')
 *
 * // 条件渲染
 * {hasPermission('pms:task:create') && <Button>Create Task</Button>}
 */
export function usePermission() {
  const permissions = useAppSelector(state => state.user.permissions)
  const isSuperuser = useAppSelector(state => state.user.isSuperuser)

  /**
   * 检查当前用户是否拥有指定权限
   */
  const hasPermission = useCallback((code: string): boolean => {
    // superuser 直接通过
    if (isSuperuser) return true

    return permissions.some(p => {
      // 精确匹配
      if (p === code) return true
      // 超管通配符
      if (p === '*:*:*') return true
      // 通配符匹配（pms:* / pms:task:* 等）
      if (p.includes('*')) return matchesWildcard(code, p)
      return false
    })
  }, [permissions, isSuperuser])

  /**
   * 检查当前用户是否拥有所有指定权限
   */
  const hasAllPermissions = useCallback((...codes: string[]): boolean => {
    return codes.every(code => hasPermission(code))
  }, [hasPermission])

  /**
   * 检查当前用户是否拥有任一指定权限
   */
  const hasAnyPermission = useCallback((...codes: string[]): boolean => {
    return codes.some(code => hasPermission(code))
  }, [hasPermission])

  return { hasPermission, hasAllPermissions, hasAnyPermission }
}
